import React from 'react';

import type { Image } from '../../services/api';

import styles from './ImageGrid.module.css';

/**
 * ImageCard Props
 */
type ImageCardProps = {
  readonly image: Image;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  readonly selectionMode?: boolean;
  readonly isSelected?: boolean;
  readonly onToggleSelection?: () => void;
};

/**
 * ImageCard Component
 *
 * Displays a single image card with thumbnail, metadata, and action buttons
 * @param root0
 * @param root0.image
 * @param root0.onEdit
 * @param root0.onDelete
 * @param root0.selectionMode
 * @param root0.isSelected
 * @param root0.onToggleSelection
 */
const ImageCard: React.FC<ImageCardProps> = ({
  image,
  onEdit,
  onDelete,
  selectionMode = false,
  isSelected = false,
  onToggleSelection,
}) => {
  const handleClick = (): void => {
    if (selectionMode && onToggleSelection) {
      onToggleSelection();
    }
  };

  return (
    <div
      className={`${styles.imageCard} ${selectionMode ? styles.selectable : ''} ${isSelected ? styles.selected : ''}`}
      onClick={handleClick}
    >
      {selectionMode ? (
        <div className={styles.selectionOverlay}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelection}
            onClick={(e) => e.stopPropagation()}
            className={styles.checkbox}
            aria-label={`Select ${image.title ?? image.originalName}`}
          />
        </div>
      ) : null}

      <div className={styles.imageContainer}>
        <img
          src={image.thumbnailUrl ?? image.url}
          alt={image.description ?? image.originalName}
          className={styles.thumbnail}
        />
      </div>

      <div className={styles.imageInfo}>
        <div className={styles.imageName} title={image.title ?? image.originalName}>
          {image.title ?? image.originalName}
        </div>
        <div className={styles.imageDetails}>
          {image.category ? <span className={styles.category}>{image.category}</span> : null}
          <span className={styles.order}>Order: {image.order}</span>
        </div>
      </div>

      {!selectionMode && (
        <div className={styles.imageActions}>
          <button onClick={onEdit} className={styles.editButton} aria-label="Edit image">
            Edit
          </button>
          <button onClick={onDelete} className={styles.deleteButton} aria-label="Delete image">
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * ImageGrid Props
 */
type ImageGridProps = {
  readonly images: ReadonlyArray<Image>;
  readonly onEdit: (image: Image) => void;
  readonly onDelete: (id: string) => void;
  readonly selectionMode?: boolean;
  readonly selectedImages?: ReadonlySet<string>;
  readonly onToggleSelection?: (imageId: string) => void;
};

/**
 * ImageGrid Component
 *
 * Displays images in a responsive grid layout with edit and delete actions
 *
 * Features:
 * - Responsive grid layout (1-4 columns based on screen size)
 * - Image thumbnails with hover effects
 * - Metadata display (title, category, order)
 * - Edit and delete action buttons
 * - Multi-select mode with checkboxes
 * - Empty state when no images
 * @param root0
 * @param root0.images
 * @param root0.onEdit
 * @param root0.onDelete
 * @param root0.selectionMode
 * @param root0.selectedImages
 * @param root0.onToggleSelection
 */
const ImageGrid: React.FC<ImageGridProps> = ({
  images,
  onEdit,
  onDelete,
  selectionMode = false,
  selectedImages = new Set(),
  onToggleSelection,
}) => {
  // Empty state
  if (images.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>📷</div>
        <h3>No images found</h3>
        <p>Upload some images to get started!</p>
      </div>
    );
  }

  return (
    <div className={styles.imageGrid}>
      {images.map((image) => (
        <ImageCard
          key={image._id}
          image={image}
          onEdit={() => onEdit(image)}
          onDelete={() => onDelete(image._id)}
          selectionMode={selectionMode}
          isSelected={selectedImages.has(image._id)}
          onToggleSelection={() => onToggleSelection?.(image._id)}
        />
      ))}
    </div>
  );
};

export default ImageGrid;
